(() => {
  if (window.ProductCardImages) return;

  const MOVE_TOLERANCE = 8;
  const HORIZONTAL_RATIO = 1.5;
  const AMBIGUOUS_LIMIT = 18; // Briefly defer unclear input, then favour page scrolling.
  const SNAP_DISTANCE = 35;
  const FLICK_VELOCITY = 0.45; // CSS pixels per millisecond.
  const VELOCITY_WINDOW = 80;
  const CLICK_GUARD_MS = 500;

  window.ProductCardImages = class ProductCardImages {
    constructor(grid) {
      this.grid = grid;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.states = new Map();
      this.active = true;
      this.listeners = [];
      this.onOtherTouch = (event) => {
        if (event.pointerType === 'touch' && this.gesture && event.pointerId !== this.gesture.id) this.cancelGesture();
      };
      for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'lostpointercapture', 'click', 'keydown']) {
        const handler = this[type].bind(this);
        const options = type === 'click' ? true : { passive: type.startsWith('pointer') };
        grid.addEventListener(type, handler, options);
        this.listeners.push([type, handler, options]);
      }
      // One observer for the grid, never one per card. Offscreen images stay inert.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (!isIntersecting || !this.isMobileCard(target)) return;
            this.observer.unobserve(target);
            this.prepare(target);
          });
        }, { rootMargin: '500px 0px' });
      }
      this.onReset = () => {
        this.cancelGesture();
        this.states.forEach((state) => {
          state.request++;
          this.render(state, state.index, false);
        });
      };
      this.onBreakpoint = () => {
        this.onReset();
        this.suppressed = null;
        this.observer?.disconnect();
        if (this.mobile.matches) this.observeCards();
      };
      this.viewportWidth = window.innerWidth;
      this.onResize = () => {
        // iOS browser chrome changes height while scrolling; only width matters.
        if (window.innerWidth === this.viewportWidth) return;
        this.viewportWidth = window.innerWidth;
        this.onReset();
      };
      this.mobile.addEventListener('change', this.onBreakpoint);
      this.reducedMotion.addEventListener('change', this.onReset);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('pagehide', this.onReset);
      grid.classList.add('has-card-image-carousel');
      this.observeCards();
    }

    isMobileCard(card) {
      return this.active && this.mobile.matches && card.hasAttribute('data-mobile-card-carousel');
    }

    cardFor(target) {
      const card = target.closest('[data-mobile-card-carousel]');
      return card && this.grid.contains(card) && this.isMobileCard(card) ? card : null;
    }

    observeCards(roots = [this.grid]) {
      if (!this.mobile.matches || !this.observer) return;
      roots.forEach((root) => root.querySelectorAll('[data-mobile-card-carousel]').forEach((card) => {
        if (Number(card.querySelector('[data-card-image-count]')?.dataset.cardImageCount) > 1) this.observer.observe(card);
      }));
    }

    stateFor(card) {
      if (!this.states.has(card)) {
        const controls = card.querySelector('[data-card-image-count]');
        const track = controls.querySelector('[data-card-image-track]');
        const count = Number(controls.dataset.cardImageCount);
        const slots = Array.from({ length: count }, () => {
          const slot = document.createElement('span');
          slot.className = 'card-image-carousel__slide';
          return slot;
        });
        track.replaceChildren(...slots);
        this.states.set(card, {
          card, count, index: 0, position: 0, request: 0, track, slots,
          surface: controls.querySelector('[data-card-image-surface]'),
          status: controls.querySelector('[data-card-image-status]'),
          ready: new Set(), loads: new Map(),
        });
      }
      return this.states.get(card);
    }

    prepare(card) {
      if (!this.isMobileCard(card)) return;
      const state = this.stateFor(card);
      if (state.count < 2) return;
      [state.index, state.index - 1, state.index + 1].forEach((index) => {
        if (index >= 0 && index < state.count) this.load(state, index);
      });
    }

    load(state, index) {
      if (state.ready.has(index)) return Promise.resolve(true);
      if (state.loads.has(index)) return state.loads.get(index);
      const template = state.card.querySelector(`template[data-card-image="${index}"]`);
      const source = index === 0 ? state.card.querySelector('[data-card-primary-image]')
        : template.hasAttribute('data-reuse-secondary') ? state.card.querySelector('[data-card-secondary-image]')
          : template.content.querySelector('img');
      // Import out of the template's inert owner document before awaiting load.
      const image = document.importNode(source, false);
      image.className = 'card-image-carousel__image';
      image.removeAttribute('data-card-primary-image');
      image.removeAttribute('data-card-secondary-image');
      image.alt = '';
      image.draggable = false;
      image.loading = 'eager';
      if (template) image.sizes = template.dataset.imageSizes;
      const pending = new Promise((resolve) => {
        const finish = (success) => {
          image.onload = null;
          image.onerror = null;
          resolve(success);
        };
        image.onload = () => finish(image.naturalWidth > 0);
        image.onerror = () => finish(false);
        if (image.complete && image.naturalWidth > 0) finish(true);
      }).then(async (success) => {
        if (success && typeof image.decode === 'function') {
          try { await image.decode(); } catch { success = false; }
        }
        state.loads.delete(index);
        if (!success || !this.active || !this.grid.contains(state.card)) return false;
        const gesture = this.gesture;
        const before = gesture?.dragging && gesture.state === state ? this.dragBounds(gesture) : null;
        state.slots[index].replaceChildren(image);
        state.ready.add(index);
        if (index === 0) state.surface.classList.add('is-ready');
        if (before) {
          const after = this.dragBounds(gesture);
          const desired = gesture.position - (gesture.lastX - gesture.dragX) / gesture.width;
          const blocked = !before.ready || desired < before.min || desired > before.max;
          if (blocked && (before.min !== after.min || before.max !== after.max || before.ready !== after.ready)) this.rebaseDrag(gesture);
        }
        return true;
      });
      state.loads.set(index, pending);
      return pending;
    }

    render(state, position, animate) {
      state.position = position;
      state.track.style.transition = animate && !this.reducedMotion.matches ? '' : 'none';
      state.track.style.transform = `translate3d(${-position * 100}%, 0, 0)`;
    }

    visiblePosition(state, width) {
      // Grab an interrupted CSS snap at its visible position, not its destination.
      const matrix = getComputedStyle(state.track).transform.match(/^matrix(3d)?\((.+)\)$/);
      const position = matrix ? -Number(matrix[2].split(',')[matrix[1] ? 12 : 4]) / width : state.position;
      return Math.max(0, Math.min(position, state.count - 1));
    }

    dragBounds(gesture) {
      const { state, startIndex } = gesture;
      let min = startIndex;
      let max = startIndex;
      const ready = state.ready.has(startIndex);
      if (ready && min > 0 && state.ready.has(min - 1)) min--;
      if (ready && max < state.count - 1 && state.ready.has(max + 1)) max++;
      return { min, max, ready };
    }

    rebaseDrag(gesture) {
      gesture.position = gesture.state.position;
      gesture.dragX = gesture.lastX;
      gesture.samples = [{ x: gesture.lastX, time: gesture.lastTime }];
    }

    activateDrag(gesture) {
      const width = gesture.surface.getBoundingClientRect().width;
      if (!width) return;
      const position = this.visiblePosition(gesture.state, width);
      gesture.width = width;
      gesture.startIndex = Math.round(position);
      gesture.startPosition = position;
      gesture.dragging = true;
      this.render(gesture.state, position, false);
      this.rebaseDrag(gesture);
    }

    recentVelocity(gesture) {
      const samples = gesture.samples;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const cutoff = last.time - VELOCITY_WINDOW;
      const startTime = Math.max(first.time, cutoff);
      if (last.time <= startTime) return 0;
      let startX = first.x;
      if (first.time < cutoff && samples[1].time > first.time) {
        startX += (samples[1].x - first.x) * (cutoff - first.time) / (samples[1].time - first.time);
      }
      return (last.x - startX) / (last.time - startTime);
    }

    pointerdown(event) {
      if (event.pointerType !== 'touch') return;
      if (event.isPrimary === false || (this.gesture && this.gesture.id !== event.pointerId)) {
        this.cancelGesture();
        return;
      }
      const surface = event.target.closest('[data-card-image-surface]');
      const card = surface && this.cardFor(surface);
      if (!card) return;
      const state = this.stateFor(card);
      if (state.count < 2) return;
      this.prepare(card);
      this.suppressed = null;
      state.request++;
      this.gesture = {
        card, surface, state, originIndex: state.index,
        id: event.pointerId, x: event.clientX, y: event.clientY, axis: null, moved: false,
        lastX: event.clientX, lastTime: event.timeStamp, dragging: false,
      };
      window.addEventListener('pointerdown', this.onOtherTouch, { passive: true });
    }

    pointermove(event) {
      const gesture = this.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) > MOVE_TOLERANCE) {
        gesture.moved = true;
        if (!gesture.axis) {
          if (Math.abs(dx) > HORIZONTAL_RATIO * Math.abs(dy)) gesture.axis = 'x';
          else if (Math.abs(dy) > HORIZONTAL_RATIO * Math.abs(dx) || Math.max(Math.abs(dx), Math.abs(dy)) >= AMBIGUOUS_LIMIT) gesture.axis = 'y';
        }
      }
      gesture.lastX = event.clientX;
      gesture.lastTime = event.timeStamp;
      if (gesture.axis !== 'x') return;
      if (!gesture.dragging) {
        this.activateDrag(gesture);
        return;
      }
      const bounds = this.dragBounds(gesture);
      if (!bounds.ready) {
        this.rebaseDrag(gesture);
        return;
      }
      gesture.samples.push({ x: event.clientX, time: event.timeStamp });
      while (gesture.samples.length > 2 && gesture.samples[1].time <= event.timeStamp - VELOCITY_WINDOW) gesture.samples.shift();
      const position = Math.max(bounds.min, Math.min(bounds.max, gesture.position - (event.clientX - gesture.dragX) / gesture.width));
      this.render(gesture.state, position, false);
    }

    pointerup(event) {
      const gesture = this.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      this.pointermove(event);
      this.gesture = null;
      window.removeEventListener('pointerdown', this.onOtherTouch);
      if (gesture.moved) this.guardClick(gesture.surface);
      if (!this.isMobileCard(gesture.card) || !gesture.dragging) return;
      const bounds = this.dragBounds(gesture);
      const position = gesture.state.position;
      const displacement = position - gesture.startPosition;
      const distance = Math.abs(displacement) * gesture.width;
      const velocity = this.recentVelocity(gesture);
      const fast = distance > MOVE_TOLERANCE && Math.abs(velocity) >= FLICK_VELOCITY && Math.sign(-velocity) === Math.sign(displacement);
      const far = distance >= Math.max(SNAP_DISTANCE, gesture.width * 0.2);
      let destination = Math.round(position);
      if (far || fast) destination = displacement > 0 ? Math.ceil(position) : Math.floor(position);
      const target = Math.max(bounds.min, Math.min(bounds.max, destination));
      this.snap(gesture.state, target);
    }

    snap(state, index, animate = true) {
      const changed = state.index !== index;
      state.index = index;
      this.render(state, index, animate);
      if (changed && state.status) state.status.textContent = state.status.dataset.positionLabel.replace(/\[index\]/g, String(index + 1));
      this.prepare(state.card);
    }

    pointercancel(event) {
      if (this.gesture?.id === event.pointerId) this.cancelGesture();
    }

    lostpointercapture(event) {
      this.pointercancel(event);
    }

    cancelGesture() {
      if (this.gesture) {
        this.guardClick(this.gesture.surface);
        if (this.gesture.dragging) this.snap(this.gesture.state, this.gesture.originIndex);
      }
      this.gesture = null;
      window.removeEventListener('pointerdown', this.onOtherTouch);
    }

    guardClick(surface) {
      this.suppressed = { surface, until: Date.now() + CLICK_GUARD_MS };
    }

    click(event) {
      const surface = event.target.closest('[data-card-image-surface]');
      if (event.detail !== 0 && surface && this.suppressed?.surface === surface && Date.now() <= this.suppressed.until) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressed = null;
      }
    }

    keydown(event) {
      const surface = event.target.closest('[data-card-image-surface]');
      const card = surface && this.cardFor(surface);
      if (!card || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const state = this.stateFor(card);
      if (state.count < 2) return;
      event.preventDefault();
      this.cancelGesture();
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? state.count - 1 : state.index + (event.key === 'ArrowRight' ? 1 : -1);
      this.show(card, target);
    }

    async show(card, requestedIndex) {
      if (!this.isMobileCard(card)) return false;
      const state = this.stateFor(card);
      const index = Math.max(0, Math.min(requestedIndex, state.count - 1));
      const request = ++state.request;
      // Keyboard Home/End can span images; never animate over unloaded slots.
      const first = Math.min(state.index, index);
      const last = Math.max(state.index, index);
      const loaded = await Promise.all(Array.from({ length: last - first + 1 }, (_, offset) => this.load(state, first + offset)));
      if (loaded.some((success) => !success) || !this.isMobileCard(card) || !this.grid.contains(card) || request !== state.request) return false;
      this.snap(state, index);
      return true;
    }

    destroy() {
      this.active = false;
      this.cancelGesture();
      this.observer?.disconnect();
      this.listeners.forEach(([type, handler, options]) => this.grid.removeEventListener(type, handler, options));
      this.mobile.removeEventListener('change', this.onBreakpoint);
      this.reducedMotion.removeEventListener('change', this.onReset);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pagehide', this.onReset);
      this.grid.classList.remove('has-card-image-carousel');
      this.states.clear();
    }
  };
})();
